import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ButtonDeleteIds } from 'components/ButtonDeleteIds';
import { DeleteItemFrom } from 'components/DeleteItemFrom';
import { TableTopPanel } from 'components/TableTopPanel';
import { AG_GRID_ICON_SETUP } from 'constantsProject';
import { BimChapterEnum, type WorkOrderChangeStatusEnum } from 'gql/graphql';
import { BimWorkOrderPriceRowDetails } from 'pages/Project/BimProject/BimCommonComponents/BimWorkOrderPriceRowDetails';
import { getRowClass } from 'pages/Project/BimProject/BimCommonComponents/WorkOrderPriceCellRenderers';
import { useHighLightRulesWithRequestData } from 'pages/Project/Common/hooks/useHighLightRulesWithRequestData';
import { destroyAgGridTableFilters } from 'utils';

import type { RowClickedEvent } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type { ForwardedRef } from 'react';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';

import { BimWorkOrderRowTypeEnum } from '../../WorkOrder/types';
import { useBimWorkOrderChangePriceStates } from '../BimWorkOrderChange/hooks';
import { useBimWorkOrderChangeTotals } from '../hooks';

import {
    useDeleteWorkOrderChangeOtherPrice,
    useExcludeWorkOrderChangeOtherPrice,
    useWorkOrderChangeOtherColumns,
    useWorkOrderChangeOtherPriceList,
} from './hooks';

interface ExtendedAgGridReact {
    api: {
        refreshInfiniteCache: () => void;
    };
}

interface IBimWOrkOrderChangeOtherTable {
    workOrderChangeId: number;
    workOrderRootObjectId: number | undefined;
    workOrderChangeRootObjectId: number | undefined;
    constructionTypeId: number | undefined;
    workOrderChangeStatus: WorkOrderChangeStatusEnum | undefined;
    makeRefreshTable: () => void;
}

const defaultColDef = {
    resizable: true,
    wrapHeaderText: true,
    spanHeaderHeight: true,
};

export const BimWorkOrderChangeOthersTable = forwardRef(
    (props: IBimWOrkOrderChangeOtherTable, ref: ForwardedRef<ExtendedAgGridReact>) => {
        const innerGridRef = useRef<AgGridReact<any> | null>(null);

        const {
            workOrderRootObjectId,
            workOrderChangeRootObjectId,
            workOrderChangeId,
            constructionTypeId,
            workOrderChangeStatus,
            makeRefreshTable,
        } = props;

        const {
            bimWorkOrderChangePriceStatesData: { hasExcessCompletedOthers },
            bimWorkOrderChangePriceStatesMakeRequest,
        } = useBimWorkOrderChangePriceStates({
            workOrderChangeId,
            chapter: BimChapterEnum.Others,
            workOrderObjectId: workOrderRootObjectId,
            workOrderChangeObjectId: workOrderChangeRootObjectId,
        });

        const {
            tableItems,
            totalItems,
            fetchedItemsCounter,
            changeFilterHandler,
            refreshWorkOrderOtherPriceList: refreshTableRowsData,
        } = useWorkOrderChangeOtherPriceList({
            workOrderChangeId,
            workOrderRootObjectId,
            workOrderChangeRootObjectId,
        });

        const refreshWorkOrderOtherPriceList = useCallback(() => {
            refreshTableRowsData();
            bimWorkOrderChangePriceStatesMakeRequest();
            destroyAgGridTableFilters({
                api: innerGridRef?.current?.api,
                columns: ['name'],
            });
        }, [bimWorkOrderChangePriceStatesMakeRequest, refreshTableRowsData]);

        const {
            fullSetColumns,
            resetCheckedRows,
            checkedRows,
            isSingleDeleteModalOpened,
            closeSingleModalHandler,
            isExcludeModalOpened,
            totalDetailsOpened,
            closeTotalDetails,
            detailsColumns,
            tableSettingsConfirm,
            fullSetSettingsColumns,
            isColumnVisible,
        } = useWorkOrderChangeOtherColumns({
            workOrderChangeId,
            workOrderRootObjectId,
            workOrderChangeRootObjectId,
            changeFilterHandler,
            hasExcessCompletedOthers,
            constructionTypeId,
            refreshTable: makeRefreshTable,
            workOrderChangeStatus,
        });

        /*
         * данные для строки итогов
         */
        const { rowTotalsData } = useBimWorkOrderChangeTotals({
            workOrderChangeId,
            chapter: BimChapterEnum.Others,
            workOrderObjectId: workOrderRootObjectId,
            workOrderChangeObjectId: workOrderChangeRootObjectId,
            constructionTypeId,
            priceLevel: undefined,
        });

        /*
         * данные для полных итогов (модалка)
         */
        const totalDetailsOtherRowType = useMemo(() => {
            if (totalDetailsOpened) {
                return BimWorkOrderRowTypeEnum.TotalsString;
            }

            return undefined;
        }, [totalDetailsOpened]);
        const { rowTotalsData: rowTotalDetailsData, isLoading: totalDetailsLoading } = useBimWorkOrderChangeTotals({
            workOrderChangeId,
            chapter: BimChapterEnum.Others,
            workOrderObjectId: workOrderRootObjectId,
            workOrderChangeObjectId: workOrderChangeRootObjectId,
            constructionTypeId,
            useInPrices: false,
            detailsRowType: totalDetailsOtherRowType,
            priceLevel: undefined,
        });

        const onDeleteSuccess = useCallback(() => {
            makeRefreshTable();
            resetCheckedRows();
        }, [makeRefreshTable, resetCheckedRows]);

        const { deleteSetData, deleteStatus, deleteError, deleteData, deleteHandler } =
            useDeleteWorkOrderChangeOtherPrice({
                workOrderChangeId,
                checkedRows,
                onSuccess: onDeleteSuccess,
            });

        const { handleExclude, excludeStatus, excludeError, excludeData, excludeSetData } =
            useExcludeWorkOrderChangeOtherPrice({
                workOrderChangeId,
                rowData: checkedRows,
                onSuccess: onDeleteSuccess,
            });

        useImperativeHandle(
            ref,
            () => ({
                api: {
                    refreshInfiniteCache: () => {
                        refreshWorkOrderOtherPriceList();
                    },
                },
            }),
            [refreshWorkOrderOtherPriceList],
        );

        const refreshKey = useMemo(
            () => `${workOrderChangeId}_${workOrderChangeStatus}`,
            [workOrderChangeId, workOrderChangeStatus],
        );

        const { rules, rowClickHandler } = useHighLightRulesWithRequestData({
            requestIds: useMemo(() => ['addOtherToBimWorkOrderChange'], []),
            dataKeyAccessor: 'itemIds',
            baseRules: getRowClass,
            gridApiRef: innerGridRef?.current,
            refreshKey,
        });

        const handleRowClick = useCallback(
            (e: RowClickedEvent) => {
                rowClickHandler(e?.data?.id);
            },
            [rowClickHandler],
        );

        const {
            modalConfirmButtonText,
            deleteModalTextHeader,
            deleteModalTextConfirmation,
            modalCancelButtonText,
            deleteOrExcludeStatus,
            deleteOrExcludeError,
            deleteOrExcludeSetData,
            deleteOrExcludeHandler,
            deleteOrExcludeData,
        } = useMemo(() => {
            if (isExcludeModalOpened) {
                return {
                    deleteModalTextHeader: 'Исключение прочих затрат',
                    deleteModalTextConfirmation: 'Вы уверены, что хотите исключить записи из Изм. к НЗ?',
                    modalConfirmButtonText: 'Исключить',
                    modalCancelButtonText: 'Не исключать',
                    deleteOrExcludeData: excludeData,
                    deleteOrExcludeStatus: excludeStatus,
                    deleteOrExcludeError: excludeError,
                    deleteOrExcludeSetData: excludeSetData,
                    deleteOrExcludeHandler: handleExclude,
                };
            }

            return {
                deleteModalTextHeader: 'Удаление прочих затрат',
                deleteModalTextConfirmation: 'Вы уверены, что хотите удалить записи?',
                deleteOrExcludeData: deleteData,
                deleteOrExcludeStatus: deleteStatus,
                deleteOrExcludeError: deleteError,
                deleteOrExcludeSetData: deleteSetData,
                deleteOrExcludeHandler: deleteHandler,
            };
        }, [
            deleteData,
            deleteError,
            deleteHandler,
            deleteSetData,
            deleteStatus,
            excludeData,
            excludeError,
            excludeSetData,
            excludeStatus,
            handleExclude,
            isExcludeModalOpened,
        ]);

        return (
            <>
                <TableTopPanel
                    checkedRowsArr={checkedRows}
                    resetCheckedRows={resetCheckedRows}
                    totalItems={totalItems}
                    fetchedItems={fetchedItemsCounter}
                    optionalColumns={fullSetSettingsColumns}
                    handleTableSettingsConfirm={tableSettingsConfirm}
                    isColumnVisible={isColumnVisible}
                    areChildrenOnLeftSide
                    showPinColumn
                    tableApi={innerGridRef?.current}
                >
                    <ButtonDeleteIds
                        deleteData={deleteData}
                        deleteStatus={deleteStatus}
                        deleteError={deleteError}
                        deleteSetData={deleteSetData}
                        handleDelete={deleteHandler}
                        style={{ marginRight: 10 }}
                    />
                </TableTopPanel>
                <div className="ag-theme-alpine BimWorkOrderPrice BimOtherTable customHeaderColor">
                    <AgGridTable
                        rowData={tableItems}
                        columnDefs={fullSetColumns}
                        animateRows
                        rowSelection="multiple"
                        suppressRowClickSelection
                        overlayNoRowsTemplate="Нет данных"
                        defaultColDef={defaultColDef}
                        rowClassRules={rules}
                        pinnedBottomRowData={rowTotalsData}
                        ref={innerGridRef}
                        onRowClicked={handleRowClick}
                        icons={AG_GRID_ICON_SETUP}
                        suppressDragLeaveHidesColumns
                        tooltipShowDelay={200}
                        context={{
                            tableUniqueId: `others_${workOrderRootObjectId}_${workOrderChangeRootObjectId}`,
                        }}
                    />
                </div>
                {totalDetailsOpened && (
                    <BimWorkOrderPriceRowDetails
                        modalTitle="Итоги прочих затрат"
                        data={rowTotalDetailsData}
                        columns={detailsColumns}
                        onClose={closeTotalDetails}
                        isDetailsLoading={totalDetailsLoading}
                        defaultColDef={defaultColDef}
                        styleClass="BimWorkOrderOtherPriceDetails customHeaderColor"
                        priceLevel={undefined}
                    />
                )}
                <DeleteItemFrom
                    modalTextHeader={deleteModalTextHeader}
                    modalTextConfirmation={deleteModalTextConfirmation}
                    isOpened={isSingleDeleteModalOpened || isExcludeModalOpened}
                    closeDeleteModal={closeSingleModalHandler}
                    deleteData={deleteOrExcludeData}
                    deleteStatus={deleteOrExcludeStatus}
                    deleteError={deleteOrExcludeError}
                    deleteSetData={deleteOrExcludeSetData}
                    handleDelete={deleteOrExcludeHandler}
                    modalConfirmButtonText={modalConfirmButtonText}
                    modalCancelButtonText={modalCancelButtonText}
                />
            </>
        );
    },
);

BimWorkOrderChangeOthersTable.displayName = 'BimWorkOrderChangeOthersTable';
